import { useEffect, useState } from "react";

import httpClient from "api/httpClient";

// MUI
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MDInput from "components/MDInput";

// MD components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import ConfirmationDialog from "components/Common/ConfirmationDialog";

// Layout wrapper used in Material Dashboard
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { clearTokenAndRedirectToLogin } from "utils/auth";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { sprintf } from "utils/strings";
import { CustomTablePagination } from "components/Common/CustomTablePagination";
import { CustomMenuButton } from "components/Common/CustomMenuButton";

import SchemaDialogForm from "components/Form/SchemaDialogForm";
import { permissionGroupFormFields, permissionGroupRecord } from "./data/PermissionGroupSchemas";
import SchemaDataTable from "components/Common/SchemaDataTable";
import { permissionGroupsColumns } from "./data/PermissionGroupsTableSchema";
import { handleApiError } from "utils/apiError";

function PermissionGroups() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [lastPage, setLastPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(permissionGroupRecord); // null = create
  const [formDialogTitle, setFormDialogTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [snackErrorMsg, setSnackErrorMsg] = useState("");
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [snackBarColor, setSnackBarColor] = useState("error");
  const [snackBarIcon, setSnackBarIcon] = useState("warning");
  const [snackBarTitle, setSnackBarTitle] = useState(t("auth.signInErrorTitle"));
  const [itemToDelete, setItemToDelete] = useState(null);
  const [confirmationDialogMessage, setConfirmationDialogMessage] = useState("");
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [action, setAction] = useState("create");
  const closeOpenSB = () => setSnackErrorMsg(false);
  const [bulkActions, setBulkActions] = useState([
    { key: "select_all", text: "Select All", icon: "select_all", iconColor: "text" },
    { key: "unselect_all", text: "Unselect All", icon: "deselect", iconColor: "text" },
    { key: "delete_selected", text: "Delete", icon: "delete", iconColor: "error" },
  ]);
  const [searchInput, setSearchInput] = useState("");

  const handleToggleCheckbox = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Optionally clear selection when page changes:
  useEffect(() => {
    setSelectedIds([]);
  }, [page]);

  const apiBase = process.env.REACT_APP_API_URL;

  const renderSB = (
    <MDSnackbar
      color={snackBarColor}
      icon={snackBarIcon}
      title={snackBarTitle}
      content={snackErrorMsg}
      dateTime=""
      open={snackBarOpen}
      onClose={closeOpenSB}
      close={closeOpenSB}
      bgWhite
    />
  );

  let showSnackBar = (title, type, message) => {
    if (type === "error") {
      setSnackBarColor("error");
      setSnackBarIcon("warning");
    } else {
      setSnackBarColor("success");
      setSnackBarIcon("check_circle");
    }
    setSnackBarTitle(title);
    setSnackErrorMsg(message);
    setSnackBarOpen(true);
    setTimeout(() => setSnackBarOpen(false), 3000);
  };

  let onPageChange = (newPage) => {
    setPage(newPage);
  };

  let onRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSearchFieldKeyUp = (event) => {
    if (event.key === "Enter") {
      loadGroups(searchInput);
    }
  };

  const loadGroups = async (q) => {
    try {
      setLoading(true);
      let url = "";
      if (q.length > 0) {
        url = `${apiBase}/api/permission-groups/search-list?page=${page}&size=${rowsPerPage}&q=${q}`;
      } else {
        url = `${apiBase}/api/permission-groups?page=${page}&size=${rowsPerPage}`;
      }
      const res = await httpClient.get(url);
      setGroups(res.data.content || []);
      setTotalElements(res.data.totalElements || 0);
      setTotalPages(res.data.totalPages || 0);
      setLastPage(res.data.last || false);
      setRowsPerPage(res.data.size || 5);
      setPage(res.data.page || 0);
    } catch (err) {
      const { handled } = handleApiError(err, {
        showSnackBar,
        onAuthError: null, // will also be called for 401 if you want
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchOnClick = async () => {
    loadGroups(searchInput);
  };

  const loadPermission = async () => {
    try {
      const auth = localStorage.getItem("auth");
      let parsedAuth = null;
      if (auth) {
        try {
          parsedAuth = JSON.parse(auth);
        } catch (e) {
          // ignore
          return;
        }
      }
      const res = await httpClient.get(`${apiBase}/api/permissions/list`, {
        headers: {
          Authorization: `Bearer ${parsedAuth.token}`,
        },
      });
      if (res) {
        setPermissions(res.data || []);
      }
    } catch (err) {
      const { handled } = handleApiError(err, {
        showSnackBar,
        onAuthError: null, // will also be called for 401 if you want
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups("");
    loadPermission();
  }, [page]);

  const deleteBulk = async (selectedIds) => {
    try {
      const auth = localStorage.getItem("auth");
      let parsedAuth = null;
      if (auth) {
        try {
          parsedAuth = JSON.parse(auth);
        } catch {
          // ignore
          return;
        }
      }
      let res = await httpClient.post(`${apiBase}/api/permission-groups/bulk-delete`, {
        headers: {
          Authorization: `Bearer ${parsedAuth.token}`,
        },
        body: JSON.stringify({ ids: selectedIds }),
      });
      if (res.status == 204) {
        showSnackBar(
          t("successMsg.operationTitleSuccess"),
          "success",
          t("successMsg.permissionGroupDeleted")
        );
        await loadGroups("");
      }
    } catch (err) {
      const { handled } = handleApiError(err, {
        showSnackBar,
        onAuthError: null, // will also be called for 401 if you want
      });
    } finally {
      setConfirmationDialogOpen(false);
    }
  };

  const openCreateDialog = () => {
    setAction("create");
    setEditingGroup({ ...permissionGroupRecord });
    setFormDialogTitle(t("permissionGroups.createPermissionGroup"));
    setDialogOpen(true);
  };

  const openEditDialog = (group) => {
    setAction("edit");
    setEditingGroup({ ...group });
    setFormDialogTitle(t("permissionGroups.editPermissionGroup"));
    setDialogOpen(true);
  };

  const onDelete = async () => {
    try {
      const auth = localStorage.getItem("auth");
      let parsedAuth = null;
      if (auth) {
        try {
          parsedAuth = JSON.parse(auth);
        } catch {
          // ignore
          return;
        }
      }
      let res;
      if (Array.isArray(itemToDelete)) {
        res = await httpClient.post(
          `${apiBase}/api/permission-groups/bulk-delete`,
          { ids: itemToDelete },
          {
            headers: {
              Authorization: `Bearer ${parsedAuth.token}`,
            },
          }
        );
      } else {
        res = await httpClient.delete(`${apiBase}/api/permission-groups/${itemToDelete.id}`, {
          headers: {
            Authorization: `Bearer ${parsedAuth.token}`,
          },
        });
      }
      if (res.status == 204) {
        showSnackBar(
          t("successMsg.operationTitleSuccess"),
          "success",
          t("successMsg.permissionGroupDeleted")
        );
        await loadGroups("");
      }
    } catch (err) {
      const { handled } = handleApiError(err, {
        showSnackBar,
        onAuthError: null, // will also be called for 401 if you want
      });
    } finally {
      setConfirmationDialogOpen(false);
    }
  };

  const handleDelete = async (group) => {
    setItemToDelete(group);
    setConfirmationDialogMessage(sprintf(t("confirmation.toDeletePermissionGroup"), [group.name]));
    setConfirmationDialogOpen(true);
  };

  const handleDeleteBulk = async (ids) => {
    setItemToDelete(ids);
    setConfirmationDialogMessage(t("confirmation.toDeletePermissionGroups"));
    setConfirmationDialogOpen(true);
  };

  const actionsMenuItemClicked = async (key) => {
    if (key.includes("Delete")) {
      handleDeleteBulk(selectedIds);
      return;
    } else if (key.includes("Select All")) {
      handleSelectAll();
    } else if (key.includes("Unselect All")) {
      handleUnSelectAll();
    }
  };

  const handleSelectAll = () => {
    for (let i = 0; i < groups.length; i++) {
      if (selectedIds.includes(groups[i].id)) {
        continue;
      }
      handleToggleCheckbox(groups[i].id);
    }
  };

  const handleUnSelectAll = () => {
    setSelectedIds([]);
  };

  const onSubmit = async (formData) => {
    setSaving(true);
    try {
      if (formData.id && formData.id > 0) {
        await httpClient.put(`/api/permission-groups/${formData.id}`, formData);
      } else {
        await httpClient.post(`/api/permission-groups`, formData);
      }
      setDialogOpen(false);
      await loadGroups("");
      showSnackBar("Success", "success", "Saved");
    } catch (err) {
      const { handled } = handleApiError(err, {
        showSnackBar,
        onAuthError: null, // will also be called for 401 if you want
      });
    } finally {
      setSaving(false);
    }
  };

  const cols = permissionGroupsColumns({ onEdit: openEditDialog, onDelete: handleDelete });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <Card>
            <MDBox display="flex" justifyContent="space-between" alignItems="right" p={3}>
              <MDButton variant="gradient" color="info" size="small" onClick={openCreateDialog}>
                {t("buttons.createPermissionGroup")}
                <Icon>add</Icon>
              </MDButton>
              <MDBox alignItems="right" display="flex">
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDInput
                    size="small"
                    label={t("search.searchPermissionGroups")}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyUp={handleSearchFieldKeyUp}
                  />
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={handleSearchOnClick}
                  >
                    {t("search.go")}
                  </MDButton>
                </MDBox>
                <CustomMenuButton
                  items={bulkActions}
                  onItemClicked={(item) => actionsMenuItemClicked(item)}
                  buttonTitle={"Actions"}
                />
              </MDBox>
            </MDBox>
            <MDBox px={3} pb={3}>
              {loading ? (
                <MDTypography variant="button">Loading...</MDTypography>
              ) : groups.length === 0 ? (
                <MDTypography variant="button">No permission groups defined yet.</MDTypography>
              ) : (
                <div>
                  <SchemaDataTable
                    data={groups}
                    columnsSchema={cols}
                    selectable={true}
                    selectedIds={selectedIds}
                    onToggle={handleToggleCheckbox}
                  />
                  <CustomTablePagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalItems={totalElements}
                    onPageChange={(newPage) => onPageChange(newPage)}
                    onRowsPerPageChange={(rowsPerPage) => onRowsPerPageChange(rowsPerPage)}
                  />
                </div>
              )}
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>
      {renderSB}
      <ConfirmationDialog
        onCancel={() => setConfirmationDialogOpen(false)}
        onOk={onDelete}
        open={confirmationDialogOpen}
        message={confirmationDialogMessage}
        title={t("confirmation.deletePermissionGroupTitle")}
      />
      <SchemaDialogForm
        open={dialogOpen}
        title={formDialogTitle}
        schema={permissionGroupFormFields}
        initialRecord={editingGroup} // for create pass permissionGroupRecord
        saving={saving}
        onClose={() => setDialogOpen(false)}
        deps={{ permissions }}
        onSubmit={onSubmit}
      />
    </DashboardLayout>
  );
}

export default PermissionGroups;
